import React from 'react';
import FilterIndicator, { FilterIndicatorConfig } from './FilterIndicator';
import SearchHotelsFilterInput from '@app/model/flexi_sets/input/SearchHotelsFilterInput';
import Range from '@app/model/_common/Range';

interface FilterIndicatorsGroupProps {
	filter: SearchHotelsFilterInput;
	onFilter: (filter: SearchHotelsFilterInput) => void;
}

const FilterIndicatorsGroup: React.FunctionComponent<FilterIndicatorsGroupProps> = ({ 
	filter, 
	onFilter 
}) => {
	const bedroomsConfig: FilterIndicatorConfig<number> = {
		label: 'Bedrooms',
		values: filter.bedrooms,
		keyPrefix: 'bedroom-filter',
		formatValue: (value) => `${value}`,
		onRemove: (bedroom: number) => {
			filter.removeBedroom(bedroom);
			onFilter(filter);
		}
	};

	const moreThanBedroomsConfig: FilterIndicatorConfig<number> = {
		label: 'Bedrooms',
		values: filter.moreThanBedrooms ? [filter.moreThanBedrooms] : [],
		keyPrefix: 'more-than-bedrooms-filter',
		formatValue: (value) => `${value}+`,
		onRemove: () => {
			filter.removeMoreThanBedrooms();
			onFilter(filter);
		}
	};

	const bathroomsConfig: FilterIndicatorConfig<number> = {
		label: 'Bathrooms',
		values: filter.bathrooms,
		keyPrefix: 'bathroom-filter',
		formatValue: (value) => `${value}`,
		onRemove: (bathroom: number) => {
			filter.removeBathroom(bathroom);
			onFilter(filter);
		}
	};

	const moreThanBathroomsConfig: FilterIndicatorConfig<number> = {
		label: 'Bathrooms',
		values: filter.moreThanBathrooms ? [filter.moreThanBathrooms] : [],
		keyPrefix: 'more-than-bathroom-filter',
		formatValue: (value) => `${value}+`,
		onRemove: () => {
			filter.removeMoreThanBathrooms();
			onFilter(filter);
		}
	};

	const guestsConfig: FilterIndicatorConfig<number> = {
		label: 'Guests',
		values: filter.guests,
		keyPrefix: 'guest-filter',
		formatValue: (value) => `${value}`,
		onRemove: (guest: number) => {
			filter.removeGuest(guest);
			onFilter(filter);
		}
	};

	const moreThanGuestsConfig: FilterIndicatorConfig<number> = {
		label: 'Guests',
		values: filter.moreThanGuests ? [filter.moreThanGuests] : [],
		keyPrefix: 'more-than-guest-filter',
		formatValue: (value) => `${value}+`,
		onRemove: () => {
			filter.removeMoreThanGuests();
			onFilter(filter);
		}
	};

	const isSuperhostConfig: FilterIndicatorConfig<boolean> = {
		label: 'Is Superhost',
		values: filter.isSuperhost ? [filter.isSuperhost] : [],
		keyPrefix: 'superhost-filter',
		formatValue: (value) => value ? 'Yes' : 'No',
		onRemove: () => {
			filter.removeIsSuperhost();
			onFilter(filter);
		}
	};

	const ratingConfig: FilterIndicatorConfig<Range> = {
		label: 'Rating',
		values: !!filter.rating ? [filter.rating]:[],
		keyPrefix: 'rating-filter',
		formatValue: (value) => `${value.from} - ${value.to}`,
		onRemove: () => {
			filter.removeRating();
			onFilter(filter);
		}
	}

	const numberOfReviewsConfig: FilterIndicatorConfig<Range> = {
		label: 'Number of reviews',
		values: !!filter.reviewsCount ? [filter.reviewsCount]:[],
		keyPrefix: 'reviewsCount-filter',
		formatValue: (value) => `${value.from} - ${value.to}`,
		onRemove: () => {
			filter.removeReviewsCount();
			onFilter(filter);
		}
	}

	const medianPriceConfig: FilterIndicatorConfig<Range> = {
		label: 'Avg night price',
		values: !!filter.medianPrice ? [filter.medianPrice]:[],
		keyPrefix: 'medianPrice-filter',
		formatValue: (value) => `${value.from} - ${value.to}`,
		onRemove: () => {
			filter.removeMedianPrice();
			onFilter(filter);
		}
	}

	return (
		<>
			<FilterIndicator config={bedroomsConfig} />
			<FilterIndicator config={moreThanBedroomsConfig} />
			<FilterIndicator config={bathroomsConfig} />
			<FilterIndicator config={moreThanBathroomsConfig} />
			<FilterIndicator config={guestsConfig} />
			<FilterIndicator config={moreThanGuestsConfig} />
			<FilterIndicator config={isSuperhostConfig} />
			<FilterIndicator config={ratingConfig} />
			<FilterIndicator config={numberOfReviewsConfig} />
			<FilterIndicator config={medianPriceConfig} />
		</>
	);
};

export default FilterIndicatorsGroup;
